import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, ArrowUpRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLines, Reveal } from "@/components/Motion";
import { api, fmtDate } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const KIND = { event: "Event", brief: "Brief", opportunity: "Opportunity" };
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export default function OpportunityCalendar() {
  useSeo("Partnership Opportunity Calendar", "See every sponsorship deadline, event date and partnership opportunity month by month, filtered by industry.", "/opportunity-calendar");
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [industry, setIndustry] = useState("");
  const [industries, setIndustries] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const month = ym(cursor);

  useEffect(() => { api.get("/meta").then((r) => setIndustries(r.data?.industries || [])).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/opportunity-calendar", { params: { month, industry } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [month, industry]);

  const groups = useMemo(() => {
    const g = {};
    items.forEach((i) => { const k = i.industry || "Other"; (g[k] = g[k] || []).push(i); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);
  const shift = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const label = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="opportunity-calendar-page">
      <Nav />
      <section className="pt-32 pb-10 mx-auto max-w-7xl px-5 sm:px-8">
        <p className="kicker">Opportunity calendar</p>
        <MaskedLines as="h1" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A1240] tracking-tight" lines={["Plan around", <em key="w" className="italic gold-text">when you activate.</em>]} />
        <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-300">Every event, brief and opportunity on Balqis Book, laid out by month and industry.</p>
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <button onClick={() => shift(-1)} className="grid h-11 w-11 place-items-center rounded-full border border-[#2A1240]/15 hover:border-[#E0218A] transition-colors" aria-label="Previous month" data-testid="ocal-prev"><ChevronLeft className="h-5 w-5" /></button>
            <h2 className="font-display text-3xl sm:text-4xl text-[#2A1240] min-w-[12ch]" data-testid="ocal-month-label">{label}</h2>
            <button onClick={() => shift(1)} className="grid h-11 w-11 place-items-center rounded-full border border-[#2A1240]/15 hover:border-[#E0218A] transition-colors" aria-label="Next month" data-testid="ocal-next"><ChevronRight className="h-5 w-5" /></button>
          </div>
          <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="field sm:!w-64" data-testid="ocal-industry-select">
            <option value="">All industries</option>
            {industries.map((i) => <option key={i}>{i}</option>)}
          </select>
        </div>
      </section>
      <section className="pb-24 mx-auto max-w-7xl px-5 sm:px-8" data-testid="ocal-results">
        {loading && <p className="text-slate-400">Loading…</p>}
        {!loading && !groups.length && <div className="rounded-2xl border border-dashed border-[#2A1240]/15 p-12 text-center text-slate-300" data-testid="ocal-empty">Nothing scheduled for {label} yet. Try another month or industry.</div>}
        <div className="space-y-16">
          {groups.map(([ind, list]) => (
            <Reveal key={ind}>
              <div className="grid lg:grid-cols-12 gap-6" data-testid={`ocal-group-${ind.toLowerCase().replace(/\W+/g, "-")}`}>
                <div className="lg:col-span-3"><p className="font-display text-3xl text-[#8E2BC4] italic">{ind}</p><p className="mt-1 text-xs text-slate-400">{list.length} item{list.length === 1 ? "" : "s"}</p></div>
                <div className="lg:col-span-9 divide-y divide-[#2A1240]/10 border-y border-[#2A1240]/10">
                  {list.map((i) => (
                    <Link key={`${i.kind}-${i.id}`} to={i.link} className="group grid grid-cols-[4.5rem_1fr_auto] items-center gap-4 py-5" data-testid={`ocal-item-${i.kind}-${i.id}`}>
                      <div className="text-center"><p className="font-display text-3xl text-[#2A1240] leading-none">{new Date(i.date).getDate() || ""}</p><p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-slate-400">{fmtDate(i.date, { month: "short" })}</p></div>
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-[#8E2BC4]">{KIND[i.kind]}{i.type ? ` · ${i.type}` : ""}</p>
                        <p className="mt-1 font-display text-xl text-[#2A1240] group-hover:text-[#8E2BC4] transition-colors truncate">{i.title}</p>
                        <p className="mt-1 text-xs text-slate-400 flex flex-wrap gap-x-3">{i.org}{i.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{i.location}</span>}{i.end && <span>until {fmtDate(i.end)}</span>}{i.value && <span className="font-mono text-[#8E2BC4]">{i.value}</span>}</p>
                      </div>
                      <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-[#E0218A] transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
